import * as React from 'react'

import { Bag as BagIcon } from '@syconium/little-miss-figgy'

import { useFeatureFlag } from '../../../../../app/_providers/ExperimentationProvider.client'
import { useSiteWideMessaging } from '../../../../../app/_providers/SiteWideMessagingProvider.client'
import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { AccordionView as AccordionViewSection } from '../../../../../brunswick/components/page-sections/AccordionView/AccordionView'
import { MultiAddToBagService } from '../../../../../brunswick/containers/multi-add-to-bag'
import { KitEmbroideryService } from '../../../../../containers/EmbroideryService'
import { trackEvent } from '../../../../../lib/analytics'
import {
  AccordionView,
  ProductGroup,
  ProductGroupWithProductAvailability,
  Variant,
} from '../../../../../types/graphql'

import { KitAddToBag } from './KitAddToBag'
import { KitHighlights } from './KitHighlights'
import { KitProductListItem } from './KitProductListItem'
import { KitToggle } from './KitToggle'
import { AccordionViewSectionWrapper, Content, Layout, List, ShippingMessage } from './styles'
import { hasMissingSize } from './utils'

import type { KitToggle as KitToggleType } from '../../../../../__generated__/graphql/catalog/graphql'

export interface KitDetailsProps
  extends Readonly<{
    kitProductGroup: ProductGroup | undefined
    kitItemList: ReadonlyArray<ProductGroupWithProductAvailability> | undefined
    defaultColors: Record<string, string> | null
    hideIndividualProductDetailsModals: boolean
    accordion?: AccordionView
    kitToggles?: ReadonlyArray<KitToggleType>
  }> {}

export const KitDetails: React.FC<KitDetailsProps> = ({
  kitProductGroup,
  kitItemList,
  defaultColors,
  hideIndividualProductDetailsModals,
  accordion,
  kitToggles,
}) => {
  const {
    detailsSection: { freeShippingMessage, freeReturnsMessage },
  } = useTranslation()
  const { siteWidePromotionMessage: siteWidePromo } = useSiteWideMessaging()

  const [selectedVariants, setSelectedVariants] = React.useState<Variant[]>([])

  const updateSelectedVariants = React.useCallback(
    (selectedVariant: Variant, index: number) => {
      setSelectedVariants(previousValues => {
        previousValues[index] = selectedVariant
        return [...previousValues]
      })
    },
    [setSelectedVariants]
  )
  const { description, title = '' } = kitProductGroup ?? {}
  const kitProduct = kitProductGroup?.products[0]

  const hasItemWithMissingSize = hasMissingSize(selectedVariants, kitItemList?.length ?? 0)

  //line item properties for add to cart
  const price = kitProductGroup?.defaultVariant?.price || 0
  const priceExplicitFmt = kitProductGroup?.defaultVariant?.priceExplicitFmt
  const discountPriceExplicitFmt = kitProductGroup?.defaultVariant?.discountPriceExplicitFmt
  const discountPrice = kitProductGroup?.defaultVariant?.discountPrice || null
  const sku = kitProductGroup?.defaultVariant?.sku

  const isKitFinalSale = kitProduct?.finalSale || false

  const addToBagCtaIconTestDecision = useFeatureFlag<
    'atb-cta-bag-icon-test-control' | 'atb-cta-bag-icon-test-variant'
  >({
    key: 'atb-cta-bag-icon-test',
    controlVariant: 'atb-cta-bag-icon-test-control',
    ifAccessedPriorToDecisionInitialization: 'lock-pageview-to-controlVariant',
    skip: !(kitProduct && kitItemList),
  })
  const isIconVisible = addToBagCtaIconTestDecision === 'atb-cta-bag-icon-test-variant'

  const kitHighlights = kitProduct ? (
    <KitHighlights
      price={price}
      discountPrice={discountPrice}
      priceExplicitFmt={priceExplicitFmt}
      discountPriceExplicitFmt={discountPriceExplicitFmt}
      title={title}
      badges={kitProduct.badgeList}
      description={description}
      siteWidePromo={siteWidePromo}
      swPromoEligible={kitProduct.swPromoEligible}
    />
  ) : null

  const kitToggle = kitProductGroup ? (
    <KitToggle kitHandle={kitProductGroup.handle} kitToggles={kitToggles} />
  ) : null

  const kitItemListSection = kitItemList ? (
    <List data-testid='kit-product-list'>
      {kitItemList.map((item, index) => (
        <KitProductListItem
          defaultColors={defaultColors}
          index={index}
          isKitFinalSale={isKitFinalSale}
          key={item.id + index}
          kitHandle={kitProductGroup?.handle ?? ''}
          productGroup={item}
          updateSelectedVariants={updateSelectedVariants}
          hideProductDetailsModal={hideIndividualProductDetailsModals}
        />
      ))}
    </List>
  ) : null

  const kitAddToBag =
    kitProduct && kitItemList ? (
      <KitAddToBag
        price={price}
        discountPrice={discountPrice}
        isKitFinalSale={isKitFinalSale}
        hasItemWithMissingSize={hasItemWithMissingSize}
        variants={selectedVariants}
        kitName={title}
        kitSku={sku || ''}
        icon={isIconVisible && <BagIcon height={26} width={24} />}
        swPromoEligible={kitProduct.swPromoEligible}
      />
    ) : null

  const shippingMessageSection = freeShippingMessage ? (
    <ShippingMessage>
      {freeShippingMessage} {freeReturnsMessage}
    </ShippingMessage>
  ) : null
  const accordionSection =
    accordion && accordion?.sections ? (
      <AccordionViewSectionWrapper>
        <AccordionViewSection {...accordion}></AccordionViewSection>
      </AccordionViewSectionWrapper>
    ) : null
  return (
    <MultiAddToBagService.Provider>
      <KitEmbroideryService.Provider>
        <Layout {...trackEvent({ category: 'kit details', action: 'click product details' })}>
          <Content>
            {kitHighlights}
            {kitToggle}
            {kitItemListSection}
            {kitAddToBag}
            {shippingMessageSection}
            {accordionSection}
          </Content>
        </Layout>
      </KitEmbroideryService.Provider>
    </MultiAddToBagService.Provider>
  )
}

export default KitDetails
